import LogisticTopMenu from '@components/logistic/LogisticTopMenu'
import React, { useState, useCallback } from 'react'
import { Button, ButtonGroup } from 'rsuite'
import PlusIcon from '@rsuite/icons/Plus'
import TableIcon from '@rsuite/icons/Table'
import CalendarIcon from '@rsuite/icons/Calendar'
import { CourierWorkShift } from '@@partner:types:app'
import ShiftsTableView from '@components/logistic/shifts/ShiftsTableView'
import ShiftsCalendar from '@components/logistic/shifts/ShiftsCalendar'
import ShiftModal, { ShiftModalMode } from '@components/logistic/shifts/ShiftModal'
import ShiftDetailDrawer from '@components/logistic/shifts/ShiftDetailDrawer'
import './ShiftsPlanner.scss'
import { useMutateDeleteCourierWorkShift } from '@@partner:graphql/mutation/courier/deleteCourierWorkShift.gql'

type ViewMode = 'table' | 'calendar'

export default function ShiftsPlanner () {
  // Состояние режима отображения
  const [ viewMode, setViewMode ] = useState<ViewMode>('calendar')
  
  // Состояние модального окна и drawer
  const [ isModalOpen, setIsModalOpen ] = useState(false)
  const [ isDrawerOpen, setIsDrawerOpen ] = useState(false)
  const [ modalMode, setModalMode ] = useState<ShiftModalMode>('create')
  const [ selectedShift, setSelectedShift ] = useState<CourierWorkShift | null>(null)
  const [ modalInitialDate, setModalInitialDate ] = useState<Date | null>(null)

  const [ deleteShift ] = useMutateDeleteCourierWorkShift()

  // Обработчики действий
  const handleCreateShift = useCallback((initialDate?: Date) => {
    setModalMode('create')
    setSelectedShift(null)
    setModalInitialDate(initialDate || null)
    setIsModalOpen(true)
  }, [])

  const handleEditShift = useCallback((shift: CourierWorkShift) => {
    setModalMode('edit')
    setSelectedShift(shift)
    setModalInitialDate(null)
    setIsModalOpen(true)
  }, [])

  const handleViewShift = useCallback((shift: CourierWorkShift) => {
    setSelectedShift(shift)
    setIsDrawerOpen(true)
  }, [])

  const handleDeleteShift = useCallback((shift: CourierWorkShift) => {
    deleteShift({ variables: { id: shift.id } })
  }, [ deleteShift ])

  const handleDateClick = useCallback((date: Date) => {
    handleCreateShift(date)
  }, [ handleCreateShift ])

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode)
  }, [])

  return (
    <div className="ShiftsPlanner">
      <h1 className="u-heading">Планировщик смен курьеров</h1>
      <div className="logistic-top-menu">
        <LogisticTopMenu />
      </div>

      <div className="actions-bar">
        <div className="left-actions">
          <Button 
            appearance="primary" 
            startIcon={<PlusIcon />}
            onClick={() => handleCreateShift()}
          >
            Создать смену
          </Button>
          
          <ButtonGroup>
            <Button
              appearance={viewMode === 'calendar' ? 'primary' : 'default'}
              startIcon={<CalendarIcon />}
              onClick={() => handleViewModeChange('calendar')}
            >
              Календарь
            </Button>
            <Button
              appearance={viewMode === 'table' ? 'primary' : 'default'}
              startIcon={<TableIcon />}
              onClick={() => handleViewModeChange('table')}
            >
              Таблица
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {viewMode === 'table' ? (
        <ShiftsTableView
          onEditShift={handleEditShift}
          onDeleteShift={handleDeleteShift}
          onViewShift={handleViewShift}
        />
      ) : (
        <ShiftsCalendar
          onDateClick={handleDateClick}
          onEditShift={handleEditShift}
          onViewShift={handleViewShift}
        />
      )}

      <ShiftModal
        mode={modalMode}
        isOpen={isModalOpen}
        shift={selectedShift}
        initialDate={modalInitialDate}
        onClose={() => {
          setIsModalOpen(false)
          setModalInitialDate(null)
        }}
        onSave={() => {
          // Каждый компонент обновит свои данные автоматически через GraphQL
        }}
        onEdit={(shift) => {
          // Переходим в режим редактирования
          handleEditShift(shift)
        }}
      />

      <ShiftDetailDrawer
        isOpen={isDrawerOpen}
        shift={selectedShift}
        onClose={() => {
          setIsDrawerOpen(false)
          setSelectedShift(null)
        }}
        onEdit={(shift) => {
          // Закрываем drawer и открываем modal для редактирования
          setIsDrawerOpen(false)
          handleEditShift(shift)
        }}
      />
    </div>
  )
}
